import React, { Component } from 'react';

import PropTypes from 'prop-types';

import AJAX from 'common/AJAX';
import * as AuthRequests from 'common/auth/AuthRequests';
import { DisconnectAzureContext } from 'common/containers/AuthContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { IsWidgetContext } from 'common/containers/IsWidgetContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import LazyLoadedImage from 'common/LazyLoadedImage';
import OAuthFlows from 'common/OAuthFlows';
import { H1 } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import AuthButton from './AuthButton';
import ForceAdminOAuth from './ForceAdminOAuth';
import Button from './inputs/Button';
import Tappable from './Tappable';

import GoogleIcon from 'img/google-icon.svg';

import 'css/components/_SignupForm.scss';

class SignupForm extends Component {
  static propTypes = {
    hasSuccessAction: PropTypes.bool.isRequired,
    forcedMemberAuthMechanism: PropTypes.string,
    isWidget: PropTypes.bool.isRequired,
    adminLogInURL: PropTypes.string,
    removeBranding: PropTypes.bool,
    oauthProvider: PropTypes.string,
    onEmail: PropTypes.func.isRequired,
    onLogin: PropTypes.func.isRequired,
    onOAuthSignup: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
  };

  state = {
    error: null,
    loading: null,
  };

  addAzureDomain = async (oauthData) => {
    const responseData = await AJAX.post('/api/viewer/addAzureDomain', {
      ...oauthData, // azureAccessToken, azureUserID
    });

    const { error } = parseAPIResponse(responseData, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        'invalid email':
          'Invalid email. Please try again and make sure to sign in with your company email account.',
        default: 'Something went wrong, please try again later.',
      },
    });

    if (error) {
      if (error.type === 'invalid email') {
        const { disconnectAzure } = this.props;
        disconnectAzure();
      }

      this.setState({
        error: error.message,
      });
      return;
    }

    window.location.reload();
    return;
  };

  onOAuthFailure = (error) => {
    this.setState({
      error: 'Failed to connect, please try again!',
      loading: null,
    });
  };

  onOAuthContinue = (result) => {
    var OAuthFlow = OAuthFlows.getFlow(this.state.loading, 2);
    var flow = new OAuthFlow({
      onContinue: this.onOAuthContinue,
      onFailure: this.onOAuthFailure,
      onSuccess: this.onOAuthSuccess,
      redirectURL: null,
      separateWindow: null,
      shouldOpenNewTab: this.props.hasSuccessAction,
    });
    flow.connect(result);
  };

  onOAuthSuccess = (oauthData) => {
    const { viewer } = this.props;
    if (viewer.loggedOut) {
      this.login({
        ...oauthData,
        useTokenAuth: this.props.isWidget,
      });
    } else {
      throw new Error('Unsupported OAuth method used');
    }
  };

  login = async (requestData) => {
    const { error, parsedResponse, redirecting } = await AuthRequests.login(requestData);

    // Note: We'd like to show register form if we receive an error from login endpoint except 'slow down'
    if (error && error.type === 'slow down') {
      this.setState({
        error: error.message,
      });
      return;
    }

    if (redirecting) {
      return;
    }

    if (requestData.azureUserID) {
      await this.addAzureDomain(requestData);
      return;
    }

    if (parsedResponse?.user) {
      this.props.onSuccess(parsedResponse, requestData);
      return;
    }

    if (!requestData.email || !requestData.name) {
      this.props.onOAuthSignup(requestData);
      return;
    }

    this.signup(requestData);
  };

  signup = async (requestData) => {
    const { error, parsedResponse, redirecting } = await AuthRequests.signup(requestData);

    if (redirecting) {
      return;
    }

    if (!error) {
      this.props.onSuccess(parsedResponse);
      return;
    }

    this.setState({ error: error.message });
  };

  renderErrorMessage() {
    if (!this.state.error) {
      return null;
    }

    return <div className="error">{this.state.error}</div>;
  }

  render() {
    const signUpCopy = {
      headerText: this.props.removeBranding ? 'Sign up with' : 'Sign up for Canny with',
      noteText: this.props.removeBranding
        ? 'Sign up to post and vote on feedback.'
        : 'We use Canny to collect feedback from users like you. Sign up to post and vote.',
    };

    return (
      <div className="signupForm">
        <div className="topContainer">
          <H1 variant="headingMd">{signUpCopy.headerText}</H1>
          <div className="buttons">
            <AuthButton
              authType="facebook"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={<div className="icon icon-facebook" />}
            />
            <AuthButton
              authType="google"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={<LazyLoadedImage alt="google logo" src={GoogleIcon} className="google" />}
            />
            <AuthButton
              authType="github"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={<div className="icon icon-github" />}
            />
            <div className="centeredDivider">OR</div>
            <Button
              buttonType="emailButton"
              onTap={this.props.onEmail}
              value={<div className="icon icon-email"></div>}
            />
          </div>
          {this.props.forcedMemberAuthMechanism ? (
            <ForceAdminOAuth
              companyID={this.props.company?._id}
              forcedMemberAuthMechanism={this.props.forcedMemberAuthMechanism}
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              removeBranding={this.props.removeBranding}
            />
          ) : null}
          <div className="note">{signUpCopy.noteText}</div>
          {this.renderErrorMessage()}
        </div>
        <div className="accountModalCTA">
          Already have an account?{' '}
          <Tappable onTap={this.props.onLogin}>
            <span className="underline">Log in</span>
          </Tappable>
        </div>
      </div>
    );
  }
}

export default withContexts(
  {
    company: CompanyContext,
    isWidget: IsWidgetContext,
    viewer: ViewerContext,
    disconnectAzure: DisconnectAzureContext,
  },
  { forwardRef: true },
)(SignupForm);
